/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable, Injector } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService, I18nService } from '@dm/core';
import { type Observable } from 'rxjs';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

@Injectable({ providedIn: 'root' })
export class RemoteExecuteService {
    constructor(
        private injector: Injector,
        private i18n: I18nService,
    ) { }

    /**
     * 在远端设备上执行GET请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     */
    get(deviceId: number, rest: Rest, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.remoteExecute(deviceId, rest, 'GET', {}, options);
    }

    /**
     * 在远端设备上执行PUT请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     * @param body PUT请求发送的数据
     */
    put(deviceId: number, rest: Rest, body: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.remoteExecute(deviceId, rest, 'PUT', body, options);
    }

    /**
     * 在远端设备上执行POST请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     * @param body POST请求发送的数据
     */
    post(deviceId: number, rest: Rest, body: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.remoteExecute(deviceId, rest, 'POST', body, options);
    }

    /**
     * 在远端设备上执行DELETE请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     */
    delete(deviceId: number, rest: Rest, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.remoteExecute(deviceId, rest, 'DELETE', {}, options);
    }

    /**
     * 在远端设备上执行请求
     * @param deviceId 远端设备ID
     * @param rest 远端请求Rest对象
     * @param method 请求方法
     * @param body 请求发送的数据
     */
    remoteExecute(deviceId: number, rest: Rest, method: Method, body: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const http = this.injector.get(HttpService);

        const localRest = http.makeUrl('remoteExecute.post', 'V2');
        localRest.name = rest.name;

        let url = rest.url;
        if (rest.type === 'IBASE') {
            url = url.replace(/^\/deviceManager\/rest\/[^\/]+\//, '/deviceManager/rest/xxx/');
            localRest.type = 'IBASE';
        }

        const newOptions = { ...(options || {}) };
        newOptions.name = this.i18n.get(rest.excuteTitleKey || 'common.rest_remote_execute_lab', [
            newOptions.name || this.i18n.get('rest.' + rest.name, newOptions.restNameParams) + (newOptions.restNameParams ? ' ' : ''),
        ]);

        const data = {
            device_id: deviceId,
            url,
            method,
            body,
        };
        return http.post(localRest, data, newOptions);
    }
}
